import PQueue from 'p-queue';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react';

import ApiMethods from "./ApiMethods"

const FETCHCONCURRENCY = 5;

export const useUser = (userSub) => {
    const { getAccessTokenSilently } = useAuth0();
    const url = `v1/users/${userSub}`;

    return useQuery({
        queryKey: [url],
        queryFn: async () => {
            const accessToken = await getAccessTokenSilently();
            return ApiMethods.get(url, accessToken);
        },
        enabled: !!userSub,
    })
}

export const useOrganization = (organizationId) => {
    const { getAccessTokenSilently } = useAuth0();
    const url = `v1/organizations/${organizationId}`;

    return useQuery({
        queryKey: [url],
        queryFn: async () => {
            const accessToken = await getAccessTokenSilently();
            return ApiMethods.get(url, accessToken);
        },
        enabled: !!organizationId,
    })
}

export const useProjects = (organizationId) => {
    const { getAccessTokenSilently } = useAuth0();
    const url = `v1/organizations/${organizationId}/projects?extra_fields=keynome_web_url`;

    return useQuery({
        queryKey: [url],
        queryFn: async () => {
            const accessToken = await getAccessTokenSilently();
            return ApiMethods.get(url, accessToken);
        },
        enabled: !!organizationId,
    })
}

export const useSampleTypes = ({ fetchEnabled = true }) => {
    const { getAccessTokenSilently } = useAuth0();
    const url = `v1/sample_types`;

    return useQuery({
        queryKey: [url],
        queryFn: async () => {
            const accessToken = await getAccessTokenSilently();
            return ApiMethods.get(url, accessToken);
        },
        enabled: fetchEnabled,
    })
}

export const useVersions = () => {
    const { getAccessTokenSilently } = useAuth0();
    const url = "v1/versions";

    return useQuery({
        queryKey: [url],
        queryFn: async () => {
            const accessToken = await getAccessTokenSilently();
            return ApiMethods.get(url, accessToken);
        },
    })
}


export const useDatasets = (projectId) => {
    const { getAccessTokenSilently } = useAuth0();
    const url = `v1/projects/${projectId}/sequencing_runs?extra_fields=keynome_web_url,platform`;

    return useQuery({
        queryKey: [url],
        queryFn: async () => {
            const accessToken = await getAccessTokenSilently();
            return ApiMethods.get(url, accessToken);
        },
        enabled: !!projectId,
    })
}

export const useDataset = (
    runId,
    {
        queryParams = {},
        useQueryOptions = {},
    } = {}
) => {
    const { extraFields = ['keynome_web_url'] } = queryParams;
    const { getAccessTokenSilently } = useAuth0();
    const queryClient = useQueryClient();

    let url = `v1/sequencing_runs/${runId}`;
    if (extraFields) url += `?extra_fields=${extraFields}`;

    return useQuery({
        queryKey: [url],
        queryFn: async () => {
            // If polling for active analysis status (refetchInterval is set), only refetch if status is not 'succeeded'
            if (useQueryOptions.refetchInterval !== null) {
                const previousResult = queryClient.getQueryData([url]);
                if (
                  previousResult 
                    && previousResult.attributes.status === 'succeeded'
                    && previousResult.attributes.clonality_analysis_status === 'succeeded'
                ) {
                    return previousResult;
                }
            }

            const accessToken = await getAccessTokenSilently();
            return ApiMethods.get(url, accessToken);
        },
        enabled: !!runId,
        ...useQueryOptions,
    })
}

export const useSample = (sampleId) => {
    const { getAccessTokenSilently } = useAuth0();
    const url = `v1/lib_seqs/${sampleId}?extra_fields=keynome_web_url`;

    return useQuery({
        queryKey: [url],
        queryFn: async () => {
            const accessToken = await getAccessTokenSilently();
            return ApiMethods.get(url, accessToken);
        },
        enabled: !!sampleId,
        // Retry 3 times if the status code is not 404 or 403
        retry: (failureCount, error) => {
          if (failureCount > 3) {
            return false
          }

          if (error.message.includes("404") || error.message.includes("403")) {
            return false
          }

          return true
        }
    })
}

export const useSamples = (runId) => {
    const { getAccessTokenSilently } = useAuth0();
    const url = `v1/sequencing_runs/${runId}/lib_seqs`;

    return useQuery({
        queryKey: [url],
        queryFn: async () => {
            const accessToken = await getAccessTokenSilently();
            return ApiMethods.get(url, accessToken);
        },
        enabled: !!runId,
    })
}

// This is a queue to limit the number of concurrent fetches for analyses
// to avoid overwhelming the server. 
const activeAnalysisFetchQueue = new PQueue({concurrency: FETCHCONCURRENCY});

export const useActiveAnalysis = (
    sampleId_s, 
    {
        queryParams = {},
        useQueryOptions = {},
        fetchEnabled = true,
    } = {}
) => {
    const { extraFields = ['status', 'positive_species_calls_ct'] } = queryParams;

    const { getAccessTokenSilently } = useAuth0();
    const queryClient = useQueryClient();

    const sampleIds = Array.isArray(sampleId_s) ? [...new Set(sampleId_s)] : [sampleId_s];
    return useQueries({
        queries: sampleIds.map(sampleId => {
            let url = `v1/lib_seqs/${sampleId}/analyses?primary_only=True`;
            if (extraFields && extraFields.length) url += `&extra_fields=${extraFields.join(",")}`;

            return {
                queryKey: [url],
                queryFn: async () => {
                    // If polling for active analysis status (refetchInterval is set), only refetch if status is not 'succeeded'
                    if (!!useQueryOptions.refetchInterval) {
                        const previousResult = queryClient.getQueryData([url]);
                        if (previousResult && previousResult.attributes.status === 'succeeded') {
                            return previousResult;
                        }
                    }
                    
                    const accessToken = await getAccessTokenSilently();
                    const result = await activeAnalysisFetchQueue.add(async () => {
                        const result = await ApiMethods.get(url, accessToken);
                        // Add sample to analysis attributes to make it easier to
                        // identify which analysis belongs to which sample when
                        // combining results from multiple samples
                        return Array.from(result.map(analysis => ({
                            ...analysis,
                            attributes: {...analysis.attributes, sample_id: sampleId}
                        })))
                    });
                    if (result.length === 0) return null;
                    return result[0];
                },
                enabled: fetchEnabled && !!sampleId,
                ...useQueryOptions,
            }
        }),
        combine: (results) => {
            // If fetching for a single sample, return the only useQueryResult
            if (!Array.isArray(sampleId_s)) return results[0];

            return {
                data: results.map(result => result.data),
                isLoading: results.some(result => result.isLoading),
                isError: results.some(result => result.isError),
            }
        }
    })
}

// This is a queue to limit the number of concurrent fetches for identified species
// to avoid overwhelming the server. 
const identifiedSpeciesFetchQueue = new PQueue({concurrency: FETCHCONCURRENCY});

// This is a queue to limit the number of concurrent fetches for resistance genes
// to avoid overwhelming the server. 
const resistanceGenesFetchQueue = new PQueue({concurrency: FETCHCONCURRENCY});

export const useIdentifiedSpecies = (
    analysisId_s,
    queryParams = { displayableOnly: true, extraFields: ['reasons_for_qc_failure', 'is_virus']}
) => {
    const { getAccessTokenSilently } = useAuth0();
    const { extraFields, displayableOnly } = queryParams;

    let queryParamsList = [];
    if (displayableOnly) queryParamsList.push("displayable_only=true");
    if (extraFields) queryParamsList.push(`extra_fields=${extraFields.join(",")}`);

    const analysisIds = Array.isArray(analysisId_s) ? [...new Set(analysisId_s)] : [analysisId_s];
    return useQueries({
        queries: analysisIds.map(analysisId => {
            let url = `v1/analyses/${analysisId}/identified_species`;
            if (queryParamsList.length > 0) url += `?${queryParamsList.join("&")}`;

            return {
                queryKey: [url],
                queryFn: async () => {
                    const accessToken = await getAccessTokenSilently();
                    return identifiedSpeciesFetchQueue.add(() => {
                        return ApiMethods.get(url, accessToken);
                    });
                },
                enabled: !!analysisId,
            }
        }),
        combine: (results) => {
            // If fetching for a single analysis, return the only useQueryResult
            if (!Array.isArray(analysisId_s)) return results[0];

            return {
                data: results.flatMap(result => result.data),
                isLoading: results.some(result => result.isLoading),
                isError: results.some(result => result.isError),
            }
        }
    })
}

// This is a queue to limit the number of concurrent fetches for AMR predictions
// to avoid overwhelming the server. 
const amrPredictionsFetchQueue = new PQueue({concurrency: FETCHCONCURRENCY});

export const useAmrPredictions = (speciesId_s) => {
    const { getAccessTokenSilently } = useAuth0();

    const speciesIds = Array.isArray(speciesId_s) ? [...new Set(speciesId_s)] : [speciesId_s];
    return useQueries({
        queries: speciesIds.map(speciesId => {
            const url = `v1/identified_species/${speciesId}/amr_model_predictions`;
            return {
                queryKey: [url],
                queryFn: async () => {
                    const accessToken = await getAccessTokenSilently();
                    return amrPredictionsFetchQueue.add(async () => {
                        const result = await ApiMethods.get(url, accessToken);
                        // Add identified species ID to prediction attributes to make it easier to
                        // identify which prediction belongs to which identified species when
                        // combining results from multiple species
                        return Array.from(result
                            .filter(prediction => prediction.attributes.model_performance_label !== "L")
                            .map(prediction => ({
                                ...prediction,
                                attributes: {...prediction.attributes, identified_species_id: speciesId}
                            })))
                    });
                },
                enabled: !!speciesId,
            }
        }),
        combine: (results) => {
            // If fetching for a single species, return the only useQueryResult
            if (!Array.isArray(speciesId_s)) return results[0];

            return {
                data: results.flatMap(result => result.data),
                isLoading: results.some(result => result.isLoading),
                isError: results.some(result => result.isError),
            }
        }
    })
}

export const useResistanceGenes = (speciesId_s, queryParams = {}, fetchEnabled = true) => {
    const { getAccessTokenSilently } = useAuth0();
    const { displayableHierarchyNodesOnly } = queryParams;

    const speciesIds = Array.isArray(speciesId_s) ? [...new Set(speciesId_s)] : [speciesId_s];
    return useQueries({
        queries: speciesIds.map(speciesId => {
            let url = `v1/identified_species/${speciesId}/resistance_genes`;
            if (displayableHierarchyNodesOnly) url += `?displayable_hierarchy_nodes_only=true`;
            return {
                queryKey: [url],
                queryFn: async () => {
                    const accessToken = await getAccessTokenSilently();
                    return resistanceGenesFetchQueue.add(() => {
                        return ApiMethods.get(url, accessToken);
                    });
                },
                enabled: fetchEnabled && !!speciesId,
            }
        }),
        combine: (results) => {
            // If fetching for a single species, return the only useQueryResult
            if (!Array.isArray(speciesId_s)) return results[0];

            return {
                data: results.flatMap(result => result.data),
                isLoading: results.some(result => result.isLoading),
                isError: results.some(result => result.isError),
            }
        }
    })
}

// This is a queue to limit the number of concurrent fetches for imputations
// to avoid overwhelming the server. 
const imputationFetchQueue = new PQueue({concurrency: FETCHCONCURRENCY});

export const useImputations = (amrPredictionIds, fetchEnabled = true) => {
    const { getAccessTokenSilently } = useAuth0();

    const dedupedAmrPredictionIds = [...new Set(amrPredictionIds || [])];
    return useQueries({
        queries: dedupedAmrPredictionIds.map(predictionId => {
            const url = `v1/amr_model_predictions/${predictionId}/imputations`;
            return {
                queryKey: [url],
                queryFn: async () => {
                    const accessToken = await getAccessTokenSilently();
                    return imputationFetchQueue.add(() => {
                        return ApiMethods.get(url, accessToken);
                    });
                },
                enabled: fetchEnabled && !!predictionId,
            }
        }),
        combine: (results) => {
            return {
                data: results.flatMap(result => result.data),
                isLoading: results.some(result => result.isLoading),
                isError: results.some(result => result.isError),
                isFetched: results.every(result => result.isFetched),
            }
        }
    })
}


export const useClonalityComparisons = (sampleId) => {
    const { getAccessTokenSilently } = useAuth0();
    const url = `v1/lib_seqs/${sampleId}/clonality_comparisons`;

    return useQuery({
        queryKey: [url],
        queryFn: async () => {
            const accessToken = await getAccessTokenSilently();
            return ApiMethods.get_with_included(url, accessToken);
        },
        enabled: !!sampleId,
    })
}
